import { useState, useEffect, useMemo, useRef, useCallback } from "react";
import { api } from "../../api/client";
import { useContacts } from "../../contexts/ContactsContext";
import { useInvoices } from "../../contexts/InvoicesContext";
import { useClientHealth } from "../../hooks/useClientHealth";
import HealthScoreBadge from "./HealthScoreBadge";
import ClientHealthScore from "./ClientHealthScore";
import WorkflowEditor from "../WorkflowEditor";
import CustomDropdown from "../shared/CustomDropdown";
import HelpButton from '../../tours/HelpButton';
import useAutoTour from '../../tours/useAutoTour';
import ConfirmDeleteModal from '../shared/ConfirmDeleteModal';

const NAVY   = "#0C2D48";
const BORDER = "#e0e0e0";
const FONT   = "'Raleway', sans-serif";

const INTERACTION_TYPES = [
  { value: "email",   label: "✉️ Email"   },
  { value: "call",    label: "📞 Call"    },
  { value: "meeting", label: "🤝 Meeting" },
  { value: "note",    label: "📝 Note"    },
];

const TAG_COLORS = {
  Client:   { bg: "#e8f4fb", text: "#2a6080",  border: "#82B3CC" },
  Press:    { bg: "#fdf3e8", text: "#7a4a10",  border: "#FBC490" },
  Team:     { bg: "#eaf6ef", text: "#1e6645",  border: "#6abf85" },
  Vendor:   { bg: "#f3f0fb", text: "#5b3fa0",  border: "#b39ddb" },
  Partner:  { bg: "#fff8ee", text: "#7a4a10",  border: "#FBC490" },
  Prospect: { bg: "#fef9e7", text: "#7d6608",  border: "#f9e547" },
  Media:    { bg: "#fce4ec", text: "#880e4f",  border: "#f48fb1" },
  Investor: { bg: "#e8fde8", text: "#1b5e20",  border: "#81c784" },
  Lead:     { bg: "#f3f0fb", text: "#5b3fa0",  border: "#b39ddb" },
};

const PRESET_TAGS = ["Client", "Press", "Team", "Vendor", "Partner", "Prospect", "Media", "Investor"];

const BOARD_TYPES = [
  { key: "NEW_BUSINESS", title: "New business pipeline", sub: "Track leads through to won/lost"          },
  { key: "RETENTION",    title: "Retention & renewals",  sub: "Manage returning and renewing clients"    },
  { key: "EVENT_TOUR",   title: "Event & tour pipeline", sub: "Manage enquiries to completed events"     },
  { key: "GENERAL",      title: "General board",         sub: "Flexible pipeline for any purpose"        },
  { key: "CUSTOM",       title: "Custom",                sub: "Define your own stages from scratch"      },
];

// ─── helpers ──────────────────────────────────────────────────────────────────
function fmtTimeAgo(dt) {
  if (!dt) return "Never";
  const secs = Math.floor((Date.now() - new Date(dt)) / 1000);
  if (secs < 60)    return "Just now";
  if (secs < 3600)  return `${Math.floor(secs / 60)}m ago`;
  if (secs < 86400) return `${Math.floor(secs / 3600)}h ago`;
  const d = Math.floor(secs / 86400);
  return d === 1 ? "Yesterday" : `${d} days ago`;
}

function initContactForm(c = null) {
  return {
    name: c?.name ?? "", email: c?.email ?? "", phone: c?.phone ?? "",
    company: c?.company ?? "", tags: c?.tags ?? [], notes: c?.notes ?? "",
    isNew: c?.isNew ?? false, source: c?.source ?? "manual", customTag: "",
  };
}

// ─── TagPill ──────────────────────────────────────────────────────────────────
function TagPill({ tag, onRemove }) {
  const colors = TAG_COLORS[tag] || { bg: "#f0f0f0", text: "#555", border: "#ccc" };
  return (
    <span style={{
      display: "inline-flex", alignItems: "center", gap: "0.2rem",
      background: colors.bg, color: colors.text, border: `1px solid ${colors.border}`,
      borderRadius: 10, padding: "0.1rem 0.5rem", fontSize: "0.72rem", fontWeight: 600, whiteSpace: "nowrap",
    }}>
      {tag}
      {onRemove && (
        <button onClick={onRemove} style={{ background: "none", border: "none", cursor: "pointer", color: colors.text, padding: 0, lineHeight: 1, fontSize: "0.8rem", marginLeft: 1 }}>×</button>
      )}
    </span>
  );
}

// ─── NudgeIcon ────────────────────────────────────────────────────────────────
const NUDGE_URGENCY_COLOR = { high: "#E96A6A", medium: "#f59e0b", low: "#aaa" };
function NudgeIcon({ urgency, reason }) {
  const color = NUDGE_URGENCY_COLOR[urgency] || "#aaa";
  return (
    <svg width={13} height={13} viewBox="0 0 24 24" fill={color} style={{ flexShrink: 0, verticalAlign: "middle", cursor: "default" }} title={reason}>
      <path d="M1 21h22L12 2 1 21zm12-3h-2v-2h2v2zm0-4h-2v-4h2v4z" />
    </svg>
  );
}

// ─── UpgradeGate ──────────────────────────────────────────────────────────────
function UpgradeGate({ onUpgrade }) {
  return (
    <div style={{ maxWidth: 560, margin: "5rem auto", textAlign: "center", padding: "3rem 2.5rem", background: "#fff", border: "1px solid #e0e0e0", borderRadius: 16, boxShadow: "0 4px 24px rgba(12,45,72,0.08)", fontFamily: FONT }}>
      <div style={{ fontSize: "2.5rem", marginBottom: "1rem" }}>📋</div>
      <h2 style={{ margin: "0 0 0.5rem", fontSize: "1.5rem", color: NAVY }}>CRM is a Pro feature</h2>
      <p style={{ margin: "0 0 1.5rem", color: "#666", fontSize: "0.95rem", lineHeight: 1.6 }}>
        Upgrade to Pro to unlock the full CRM suite — Kanban pipeline, client health scores, interaction history, and smart follow-up tracking.
      </p>
      <button onClick={onUpgrade} style={{ padding: "0.75rem 2rem", background: NAVY, color: "#fff", border: "none", borderRadius: 8, fontWeight: 700, fontSize: "1rem", cursor: "pointer", fontFamily: FONT }}>
        Upgrade to Pro →
      </button>
    </div>
  );
}

// ─── AddInteractionModal ──────────────────────────────────────────────────────
function AddInteractionModal({ onSave, onClose }) {
  const [type, setType]       = useState("email");
  const [date, setDate]       = useState(new Date().toISOString().slice(0, 16));
  const [summary, setSummary] = useState("");
  function handleSave() {
    if (!summary.trim()) return;
    onSave({ id: Date.now(), type, date: new Date(date).toISOString(), summary: summary.trim() });
  }
  return (
    <div className="modal-overlay">
      <div className="modal-content neutral" style={{ maxWidth: 400, width: "90vw" }}>
        <button className="close-button" onClick={onClose}>✕</button>
        <h3 style={{ marginBottom: "1.25rem" }}>Log Interaction</h3>
        <div style={{ display: "flex", flexDirection: "column", gap: "0.75rem" }}>
          <label style={labelStyle}>
            Type
            <select value={type} onChange={e => setType(e.target.value)} style={{ ...inputStyle, fontFamily: FONT }}>
              {INTERACTION_TYPES.map(i => <option key={i.value} value={i.value}>{i.label}</option>)}
            </select>
          </label>
          <label style={labelStyle}>
            Date &amp; Time
            <input type="datetime-local" value={date} onChange={e => setDate(e.target.value)} style={inputStyle} />
          </label>
          <label style={labelStyle}>
            Summary <span style={{ color: "#E96A6A" }}>*</span>
            <textarea value={summary} onChange={e => setSummary(e.target.value)} rows={3} placeholder="What happened?" style={{ ...inputStyle, resize: "vertical" }} />
          </label>
        </div>
        <div style={{ display: "flex", gap: "0.75rem", marginTop: "1.25rem" }}>
          <button onClick={handleSave} style={btnPrimary} disabled={!summary.trim()}>Log</button>
          <button onClick={onClose} style={btnGhost}>Cancel</button>
        </div>
      </div>
    </div>
  );
}

// ─── ContactFormModal ─────────────────────────────────────────────────────────
function ContactFormModal({ onSave, onClose }) {
  const [form, setForm] = useState(initContactForm());
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState("");
  const isMobileLayout = typeof window !== "undefined" && (window.innerWidth <= 1024 || window.matchMedia("(pointer: coarse)").matches);

  function set(field, value) { setForm(f => ({ ...f, [field]: value })); }
  function toggleTag(tag) {
    setForm(f => ({ ...f, tags: f.tags.includes(tag) ? f.tags.filter(t => t !== tag) : [...f.tags, tag] }));
  }
  function addCustomTag() {
    const t = form.customTag.trim();
    if (!t || form.tags.includes(t)) return;
    setForm(f => ({ ...f, tags: [...f.tags, t], customTag: "" }));
  }

  async function handleSave() {
    if (!form.name.trim()) return;
    setSaving(true);
    setSaveError("");
    try {
      await onSave({ name: form.name.trim(), email: form.email.trim(), phone: form.phone.trim(), company: form.company.trim(), tags: form.tags, notes: form.notes.trim(), isNew: form.isNew, source: form.source });
    } catch (err) {
      setSaveError(err.message || "Failed to save contact. Please try again.");
    } finally { setSaving(false); }
  }

  return (
    <div className="modal-overlay">
      <div className="modal-content neutral" style={{ maxWidth: isMobileLayout ? "100%" : 480, width: isMobileLayout ? "100%" : "90vw" }}>
        <button className="close-button" onClick={onClose}>✕</button>
        <h3 style={{ marginBottom: "1.25rem" }}>Add Contact</h3>
        <div style={{ display: "flex", flexDirection: "column", gap: "0.75rem" }}>
          <label style={labelStyle}>Name <span style={{ color: "#E96A6A" }}>*</span>
            <input value={form.name} onChange={e => set("name", e.target.value)} placeholder="Full name" style={inputStyle} />
          </label>
          <div style={{ display: "grid", gridTemplateColumns: isMobileLayout ? "1fr" : "1fr 1fr", gap: "0.75rem" }}>
            <label style={labelStyle}>Email<input value={form.email} onChange={e => set("email", e.target.value)} placeholder="email@example.com" style={inputStyle} /></label>
            <label style={labelStyle}>Phone<input value={form.phone} onChange={e => set("phone", e.target.value)} placeholder="+44..." style={inputStyle} /></label>
          </div>
          <label style={labelStyle}>Company<input value={form.company} onChange={e => set("company", e.target.value)} placeholder="Company name" style={inputStyle} /></label>
          <div>
            <div style={{ fontSize: "0.82rem", fontWeight: 600, color: "#555", marginBottom: "0.4rem" }}>Tags</div>
            <div style={{ display: "flex", flexWrap: "wrap", gap: "0.4rem", marginBottom: "0.5rem" }}>
              {PRESET_TAGS.map(tag => (
                <button key={tag} onClick={() => toggleTag(tag)} style={{ padding: "0.2rem 0.65rem", borderRadius: 10, border: `1px solid ${form.tags.includes(tag) ? (TAG_COLORS[tag]?.border || "#aaa") : "#ddd"}`, background: form.tags.includes(tag) ? (TAG_COLORS[tag]?.bg || "#eee") : "#fff", color: form.tags.includes(tag) ? (TAG_COLORS[tag]?.text || "#333") : "#888", fontSize: "0.78rem", fontWeight: 600, cursor: "pointer" }}>
                  {tag}
                </button>
              ))}
            </div>
            {form.tags.filter(t => !PRESET_TAGS.includes(t)).length > 0 && (
              <div style={{ display: "flex", flexWrap: "wrap", gap: "0.3rem", marginBottom: "0.4rem" }}>
                {form.tags.filter(t => !PRESET_TAGS.includes(t)).map(t => <TagPill key={t} tag={t} onRemove={() => toggleTag(t)} />)}
              </div>
            )}
            <div style={{ display: "flex", gap: "0.4rem" }}>
              <input value={form.customTag} onChange={e => set("customTag", e.target.value)} onKeyDown={e => e.key === "Enter" && addCustomTag()} placeholder="Add custom tag..." style={{ ...inputStyle, fontSize: "0.85rem", padding: "0.35rem 0.6rem", flex: 1 }} />
              <button onClick={addCustomTag} style={btnSecondary}>Add</button>
            </div>
          </div>
          <label style={labelStyle}>Notes<textarea value={form.notes} onChange={e => set("notes", e.target.value)} rows={3} placeholder="Any notes about this contact..." style={{ ...inputStyle, resize: "vertical" }} /></label>
          <label style={{ display: "flex", alignItems: "center", gap: "0.5rem", fontSize: "0.88rem", color: "#555", cursor: "pointer" }}>
            <input type="checkbox" checked={form.isNew} onChange={e => set("isNew", e.target.checked)} style={{ width: 15, height: 15 }} />
            Mark as <strong>new contact</strong>
          </label>
        </div>
        {saveError && <p style={{ color: "#E96A6A", fontSize: "0.82rem", marginTop: "0.75rem", marginBottom: 0 }}>{saveError}</p>}
        <div style={{ display: "flex", gap: "0.75rem", marginTop: "1.5rem" }}>
          <button onClick={handleSave} style={btnPrimary} disabled={!form.name.trim() || saving}>{saving ? "Saving…" : "Add Contact"}</button>
          <button onClick={onClose} style={btnGhost}>Cancel</button>
        </div>
      </div>
    </div>
  );
}

// ─── CreateCrmBoardModal ──────────────────────────────────────────────────────
function CreateCrmBoardModal({ onSave, onClose }) {
  const [name, setName]               = useState("");
  const [boardType, setBoardType]     = useState("NEW_BUSINESS");
  const [description, setDescription] = useState("");
  const [customise, setCustomise]     = useState(false);
  const [workflowDraft, setWorkflowDraft] = useState({ stages: [], columns: [] });
  const [saving, setSaving]           = useState(false);
  const [error, setError]             = useState("");

  function handleTypeSelect(key) {
    setBoardType(key);
    setCustomise(key === "CUSTOM");
  }

  const boardTypeOptions = BOARD_TYPES.map(bt => ({ value: bt.key, label: bt.title, hint: bt.sub }));

  async function handleSubmit() {
    if (!name.trim()) { setError("Board name is required."); return; }
    setSaving(true);
    try {
      await onSave({ name: name.trim(), description: description.trim(), boardType, workflowDraft: customise ? workflowDraft : null });
    } finally { setSaving(false); }
  }

  return (
    <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.45)", zIndex: 2000, display: "flex", alignItems: "center", justifyContent: "center", padding: "1rem" }} onClick={e => e.target === e.currentTarget && onClose()}>
      <div style={{ background: "#fff", borderRadius: 16, width: 560, maxWidth: "95vw", maxHeight: "92vh", overflowY: "auto", boxShadow: "0 20px 60px rgba(0,0,0,0.2)", fontFamily: FONT }}>
        <div style={{ padding: "20px 26px 16px", borderBottom: `1px solid ${BORDER}`, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <h2 style={{ margin: 0, fontSize: 17, fontWeight: 800, color: NAVY, fontFamily: "'Poppins', sans-serif" }}>New CRM Board</h2>
          <button onClick={onClose} style={{ background: "none", border: "none", fontSize: 20, cursor: "pointer", color: "#999" }}>×</button>
        </div>

        <div style={{ padding: "22px 26px", display: "flex", flexDirection: "column", gap: 18 }}>
          {error && <p style={{ margin: 0, color: "#E96A6A", fontSize: 13, fontWeight: 600 }}>{error}</p>}

          <div>
            <label style={formLabel}>Board Name *</label>
            <input autoFocus value={name} onChange={e => setName(e.target.value)} placeholder="e.g. New Artist Pipeline" style={formInput} />
          </div>

          <div>
            <label style={formLabel}>Board Type</label>
            <div style={{ marginTop: 4 }}>
              <CustomDropdown
                options={boardTypeOptions}
                value={boardType}
                onChange={handleTypeSelect}
                accentColor={NAVY}
              />
            </div>
          </div>

          <div>
            <label style={formLabel}>Description (optional)</label>
            <textarea value={description} onChange={e => setDescription(e.target.value)} rows={2} placeholder="What is this board for?" style={{ ...formInput, resize: "vertical" }} />
          </div>

          {boardType !== "CUSTOM" && (
            <div>
              <label style={{ display: "flex", alignItems: "center", gap: 10, cursor: "pointer" }}>
                <div
                  onClick={() => setCustomise(v => !v)}
                  style={{
                    width: 36, height: 20, borderRadius: 10, background: customise ? NAVY : "#ccc",
                    position: "relative", cursor: "pointer", flexShrink: 0, transition: "background 0.15s",
                  }}
                >
                  <div style={{ position: "absolute", top: 2, left: customise ? 18 : 2, width: 16, height: 16, borderRadius: "50%", background: "#fff", transition: "left 0.15s" }} />
                </div>
                <span style={{ fontSize: 13, color: "#444", fontFamily: FONT }}>Customise workflow</span>
              </label>
              {!customise && <p style={{ margin: "6px 0 0 46px", fontSize: 12, color: "#888", fontFamily: FONT }}>The system template for the selected board type will be used automatically.</p>}
            </div>
          )}

          {customise && (
            <WorkflowEditor
              mode="crm-board"
              entityId={null}
              showColumns={true}
              showAutomations={true}
              onChange={setWorkflowDraft}
            />
          )}
        </div>

        <div style={{ padding: "14px 26px", borderTop: `1px solid ${BORDER}`, display: "flex", justifyContent: "flex-end", gap: 10 }}>
          <button onClick={onClose} style={{ ...btnGhost, padding: "9px 20px", borderRadius: 8 }}>Cancel</button>
          <button onClick={handleSubmit} disabled={saving || !name.trim()} style={{ ...btnPrimary, padding: "9px 22px", borderRadius: 8, background: NAVY, color: "#fff", fontSize: 14, fontWeight: 700 }}>
            {saving ? "Creating…" : "Create Board"}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── CrmBoardSettings ─────────────────────────────────────────────────────────
function CrmBoardSettings({ board, activeBoardId, canDelete, onClose, onSaved, onDeleted }) {
  const [tab, setTab]               = useState("stages");
  const [name, setName]             = useState(board?.name || "");
  const [description, setDesc]      = useState(board?.description || "");
  const [saving, setSaving]         = useState(false);
  const [deleting, setDeleting]     = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState(false);

  async function handleSaveDetails() {
    setSaving(true);
    try {
      await api(`/api/crm-boards/${activeBoardId}`, { method: "PUT", body: JSON.stringify({ name, description }) });
      onSaved();
    } finally { setSaving(false); }
  }

  async function performDelete() {
    setDeleting(true);
    try {
      const r = await api(`/api/crm-boards/${activeBoardId}`, { method: "DELETE" });
      if (!r.ok) { const d = await r.json(); alert(d.error || "Delete failed"); return; }
      onDeleted();
    } finally { setDeleting(false); }
  }

  return (
    <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.45)", zIndex: 2000, display: "flex", alignItems: "center", justifyContent: "center", padding: "1rem" }} onClick={e => e.target === e.currentTarget && onClose()}>
      <div style={{ background: "#fff", borderRadius: 16, width: 640, maxWidth: "95vw", maxHeight: "92vh", overflowY: "auto", boxShadow: "0 20px 60px rgba(0,0,0,0.2)", fontFamily: FONT, display: "flex", flexDirection: "column" }}>
        <div style={{ padding: "20px 26px 0", borderBottom: `1px solid ${BORDER}` }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 14 }}>
            <h2 style={{ margin: 0, fontSize: 17, fontWeight: 800, color: NAVY, fontFamily: "'Poppins', sans-serif" }}>Board Settings</h2>
            <button onClick={onClose} style={{ background: "none", border: "none", fontSize: 20, cursor: "pointer", color: "#999" }}>×</button>
          </div>
          <div style={{ display: "flex", gap: 0 }}>
            {[{ key: "stages", label: "Stages & columns" }, { key: "details", label: "Board details" }].map(t => (
              <button key={t.key} onClick={() => setTab(t.key)} style={{ padding: "8px 18px", background: "none", border: "none", borderBottom: tab === t.key ? `2px solid ${NAVY}` : "2px solid transparent", fontFamily: FONT, fontSize: 13, fontWeight: tab === t.key ? 700 : 500, color: tab === t.key ? NAVY : "#888", cursor: "pointer", marginBottom: -1 }}>
                {t.label}
              </button>
            ))}
          </div>
        </div>

        <div style={{ flex: 1, overflowY: "auto", padding: "20px 26px" }}>
          {tab === "stages" && (
            <WorkflowEditor
              mode="crm-board"
              entityId={activeBoardId}
              readOnly={false}
              showColumns={true}
              showAutomations={false}
              showSaveAsTemplate={true}
              onSaved={onSaved}
            />
          )}

          {tab === "details" && (
            <div style={{ display: "flex", flexDirection: "column", gap: 16, maxWidth: 440 }}>
              <div>
                <label style={formLabel}>Board Name</label>
                <input value={name} onChange={e => setName(e.target.value)} style={formInput} />
              </div>
              <div>
                <label style={formLabel}>Description</label>
                <textarea value={description} onChange={e => setDesc(e.target.value)} rows={3} style={{ ...formInput, resize: "vertical" }} />
              </div>
              <button onClick={handleSaveDetails} disabled={saving} style={{ ...btnPrimary, padding: "9px 20px", borderRadius: 8, background: NAVY, color: "#fff", fontWeight: 700 }}>
                {saving ? "Saving…" : "Save Changes"}
              </button>

              <div style={{ marginTop: 16, paddingTop: 16, borderTop: `1px solid ${BORDER}` }}>
                <div style={{ fontSize: 13, fontWeight: 700, color: "#ef4444", marginBottom: 8 }}>Danger Zone</div>
                <button
                  onClick={() => canDelete && setDeleteConfirm(true)}
                  disabled={!canDelete || deleting}
                  title={!canDelete ? "Cannot delete the only board" : ""}
                  style={{ padding: "9px 20px", borderRadius: 8, border: "1px solid #ef4444", background: "#fff", color: !canDelete ? "#ccc" : "#ef4444", fontWeight: 600, cursor: !canDelete ? "not-allowed" : "pointer", fontFamily: FONT, fontSize: 13 }}
                >
                  {deleting ? "Deleting…" : "Delete Board"}
                </button>
                {!canDelete && <p style={{ margin: "6px 0 0", fontSize: 12, color: "#aaa" }}>You must have at least one board.</p>}
              </div>
            </div>
          )}
        </div>
      </div>
      <ConfirmDeleteModal
        open={deleteConfirm}
        onClose={() => setDeleteConfirm(false)}
        onConfirm={performDelete}
        itemType="board"
        itemName={board?.name || ''}
        warningText="This will permanently delete this board and all its contacts and data."
      />
    </div>
  );
}

// ─── BoardFieldsSection ───────────────────────────────────────────────────────
function BoardFieldsSection({ boardColumns, boardEntry, contactId, boardId, onFieldChange }) {
  const cf = boardEntry?.customFields || {};

  if (!boardColumns || boardColumns.length === 0) return null;

  function renderField(col) {
    const val = cf[col.fieldKey] ?? "";
    const onChange = v => onFieldChange(contactId, col.fieldKey, v);

    if (col.fieldType === "BOOLEAN") {
      return (
        <input type="checkbox" checked={!!val} onChange={e => onChange(e.target.checked)}
          style={{ width: 16, height: 16, marginTop: 2 }} />
      );
    }
    if (col.fieldType === "SELECT") {
      const opts = Array.isArray(col.options) ? col.options : [];
      return (
        <select value={val} onChange={e => onChange(e.target.value)} style={{ ...inputStyle, fontSize: "0.82rem", padding: "0.35rem 0.5rem" }}>
          <option value="">—</option>
          {opts.map((o, i) => <option key={i} value={typeof o === "object" ? o.value || o.label : o}>{typeof o === "object" ? o.label : o}</option>)}
        </select>
      );
    }
    if (col.fieldType === "DATE") {
      return <input type="date" value={val} onChange={e => onChange(e.target.value)} style={{ ...inputStyle, fontSize: "0.82rem", padding: "0.35rem 0.5rem" }} />;
    }
    if (col.fieldType === "NUMBER") {
      return <input type="number" value={val} onChange={e => onChange(e.target.value)} style={{ ...inputStyle, fontSize: "0.82rem", padding: "0.35rem 0.5rem" }} />;
    }
    if (col.fieldType === "TEXTAREA") {
      return <textarea value={val} onChange={e => onChange(e.target.value)} rows={2} style={{ ...inputStyle, fontSize: "0.82rem", resize: "vertical" }} />;
    }
    return <input type="text" value={val} onChange={e => onChange(e.target.value)} style={{ ...inputStyle, fontSize: "0.82rem", padding: "0.35rem 0.5rem" }} />;
  }

  return (
    <div>
      <div style={{ fontSize: "0.75rem", fontWeight: 700, color: "#aaa", textTransform: "uppercase", letterSpacing: "0.05em", marginBottom: "0.5rem" }}>Board Fields</div>
      <div style={{ display: "flex", flexDirection: "column", gap: "0.55rem" }}>
        {boardColumns.map(col => (
          <div key={col.id}>
            <div style={{ fontSize: "0.72rem", color: "#888", marginBottom: "0.15rem" }}>{col.name}</div>
            {renderField(col)}
          </div>
        ))}
      </div>
    </div>
  );
}

// ─── ContactDetailPanel ───────────────────────────────────────────────────────
function ContactDetailPanel({ contact, health, boardStages, boardColumns, boardEntry, onClose, onAddInteraction, onDeleteInteraction, onLogContact, onRefreshHealth, onBoardStageChange, onFieldChange, boardId, isMobile }) {
  const sortedInteractions = [...(contact.interactions || [])].sort((a, b) => new Date(b.date) - new Date(a.date));
  const currentStageId = boardEntry?.stageId || null;
  const currentStage   = boardStages.find(s => s.id === currentStageId);

  const [dbScore,       setDbScore]       = useState(contact.healthScore ?? null);
  const [dbScoreAt,     setDbScoreAt]     = useState(contact.healthScoreAt ?? null);
  const [recalculating, setRecalculating] = useState(false);

  useEffect(() => {
    setDbScore(contact.healthScore ?? null);
    setDbScoreAt(contact.healthScoreAt ?? null);
  }, [contact.id, contact.healthScore, contact.healthScoreAt]);

  async function handleRecalculate() {
    setRecalculating(true);
    try {
      const res = await api(`/api/contacts/${contact.id}/health-score`, { method: "POST" });
      if (res.ok) {
        const data = await res.json();
        setDbScore(data.score);
        setDbScoreAt(new Date());
      }
    } catch { /* non-critical */ }
    finally { setRecalculating(false); }
  }

  const [portal, setPortal]             = useState(null);
  const [portalActivity, setPortalActivity] = useState([]);
  const [portalLoading, setPortalLoading]   = useState(false);
  const [enablingPortal, setEnablingPortal] = useState(false);
  const [sendingLink, setSendingLink]       = useState(false);

  useEffect(() => {
    let cancelled = false;
    async function loadPortal() {
      setPortalLoading(true);
      try {
        const res = await api(`/api/portal/${contact.id}`);
        if (!res.ok) { if (!cancelled) setPortal(null); return; }
        const data = await res.json();
        if (cancelled) return;
        setPortal(data.portal || null);
        if (data.portal) {
          const actRes = await api(`/api/portal/${data.portal.id}/activity`);
          const actData = await actRes.json();
          if (!cancelled && Array.isArray(actData.activities)) setPortalActivity(actData.activities.slice(0, 5));
        }
      } catch { if (!cancelled) setPortal(null); }
      finally { if (!cancelled) setPortalLoading(false); }
    }
    loadPortal();
    return () => { cancelled = true; };
  }, [contact.id]);

  async function enablePortal() {
    setEnablingPortal(true);
    try {
      const res = await api(`/api/portal/enable/${contact.id}`, { method: "POST" });
      const data = await res.json();
      setPortal(data.portal);
    } catch { alert("Failed to enable portal."); }
    finally { setEnablingPortal(false); }
  }

  async function sendPortalLink() {
    setSendingLink(true);
    try {
      await api(`/api/portal/${portal.id}/send-link`, { method: "POST" });
      alert(`Access link sent to ${contact.email}`);
    } catch { alert("Failed to send link."); }
    finally { setSendingLink(false); }
  }

  const activityLabels = {
    portal_accessed: "🔑 Accessed portal", viewed_invoices: "🧾 Viewed invoices",
    viewed_proposals: "📄 Viewed proposals", viewed_contracts: "✍️ Viewed contracts",
    viewed_messages: "💬 Viewed messages", viewed_bookings: "📅 Viewed bookings",
    viewed_files: "📁 Viewed files", viewed_questionnaires: "📋 Viewed questionnaires",
    viewed_timesheets: "⏱ Viewed timesheets", sent_message: "💬 Sent message",
    submitted_questionnaire: "📋 Submitted questionnaire",
  };

  return (
    <div style={{ position: isMobile ? "fixed" : "relative", top: isMobile ? 0 : undefined, left: isMobile ? 0 : undefined, right: isMobile ? 0 : undefined, bottom: isMobile ? 0 : undefined, zIndex: isMobile ? 1000 : undefined, width: isMobile ? "100vw" : 340, flexShrink: 0, borderLeft: isMobile ? "none" : "1px solid #e0e0e0", background: "#fff", display: "flex", flexDirection: "column", height: isMobile ? "100vh" : "100%", overflow: "hidden" }}>
      <div style={{ padding: isMobile ? "1rem" : "1rem 1.25rem", borderBottom: "1px solid #f0f0f0", display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
        <div style={{ flex: 1, minWidth: 0, paddingRight: "0.5rem" }}>
          <h3 style={{ margin: "0 0 0.1rem", fontSize: "1.1rem", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{contact.name}</h3>
          {contact.company && <div style={{ fontSize: "0.82rem", color: "#888", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{contact.company}</div>}
        </div>
        <button onClick={onClose} style={{ background: isMobile ? "#f0f0f0" : "none", border: "none", cursor: "pointer", color: isMobile ? "#333" : "#aaa", fontSize: "1.1rem", padding: isMobile ? "0.4rem 0.7rem" : "0.1rem 0.2rem", borderRadius: isMobile ? 8 : 0, flexShrink: 0, display: "flex", alignItems: "center", justifyContent: "center", minHeight: isMobile ? 44 : undefined, minWidth: isMobile ? 44 : undefined }}>✕</button>
      </div>

      <div style={{ flex: 1, overflowY: "auto", WebkitOverflowScrolling: "touch", padding: isMobile ? "1rem" : "1rem 1.25rem", display: "flex", flexDirection: "column", gap: "1rem" }}>

        {/* Dynamic board stage selector */}
        {boardStages.length > 0 && (
          <div>
            <div style={{ fontSize: "0.75rem", fontWeight: 700, color: "#aaa", textTransform: "uppercase", letterSpacing: "0.05em", marginBottom: "0.4rem" }}>Pipeline Stage</div>
            <select
              value={currentStageId || ""}
              onChange={e => onBoardStageChange(contact.id, e.target.value)}
              style={{ width: "100%", boxSizing: "border-box", padding: "0.45rem 0.65rem", minHeight: 44, borderRadius: 7, border: `1.5px solid ${currentStage?.colour || "#ddd"}`, color: currentStage?.colour || "#333", fontWeight: 700, fontSize: "0.88rem", fontFamily: FONT, background: "#fafafa", cursor: "pointer" }}
            >
              <option value="">— Unassigned —</option>
              {boardStages.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
            </select>
          </div>
        )}

        {/* Board custom fields */}
        {boardColumns.length > 0 && (
          <BoardFieldsSection
            boardColumns={boardColumns}
            boardEntry={boardEntry}
            contactId={contact.id}
            boardId={boardId}
            onFieldChange={onFieldChange}
          />
        )}

        {/* Contact info */}
        <div style={{ display: "flex", flexDirection: "column", gap: "0.3rem" }}>
          {contact.email && <div style={{ fontSize: "0.85rem", color: "#444" }}>✉️ <a href={`mailto:${contact.email}`} style={{ color: NAVY, textDecoration: "none" }}>{contact.email}</a></div>}
          {contact.phone && <div style={{ fontSize: "0.85rem", color: "#444" }}>📞 {contact.phone}</div>}
          {contact.address && <div style={{ fontSize: "0.85rem", color: "#444" }}>📍 {contact.address}</div>}
          {(contact.tags || []).length > 0 && (
            <div style={{ display: "flex", flexWrap: "wrap", gap: "0.3rem", marginTop: "0.15rem" }}>
              {(contact.tags || []).map(t => <TagPill key={t} tag={t} />)}
            </div>
          )}
        </div>

        {/* Client health */}
        {(contact.tags || []).includes("Client") && (
          <div style={{ border: "1px solid #e0e0e0", borderRadius: 10, padding: "0.85rem 1rem", background: "#fafafa", fontFamily: FONT }}>
            <div style={{ fontSize: "0.72rem", fontWeight: 700, color: "#aaa", textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: "0.6rem" }}>Client Health</div>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "0.5rem" }}>
              <HealthScoreBadge score={dbScore} scoredAt={dbScoreAt} style={{ fontSize: "0.83rem" }} />
              <button onClick={handleRecalculate} disabled={recalculating} style={{ padding: "0.3rem 0.75rem", borderRadius: 6, border: "1px solid #ddd", background: "#fff", fontSize: "0.75rem", fontWeight: 600, color: "#555", cursor: recalculating ? "default" : "pointer", fontFamily: FONT, opacity: recalculating ? 0.6 : 1 }}>
                {recalculating ? "Calculating…" : "Recalculate"}
              </button>
            </div>
            <div style={{ fontSize: "0.78rem", color: "#888" }}>Last contacted: <strong style={{ color: "#444" }}>{fmtTimeAgo(contact.lastContactedAt)}</strong></div>
          </div>
        )}

        {(contact.tags || []).includes("Client") && health && (
          <ClientHealthScore health={health} onLogContact={onLogContact} onRefresh={onRefreshHealth} />
        )}

        {/* Notes */}
        {contact.notes && (
          <div>
            <div style={{ fontSize: "0.75rem", fontWeight: 700, color: "#aaa", textTransform: "uppercase", letterSpacing: "0.05em", marginBottom: "0.35rem" }}>Notes</div>
            <p style={{ margin: 0, fontSize: "0.87rem", color: "#555", lineHeight: 1.5, background: "#f9f9f9", borderRadius: 7, padding: "0.55rem 0.65rem" }}>{contact.notes}</p>
          </div>
        )}

        {/* Interactions */}
        <div>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "0.5rem" }}>
            <div style={{ fontSize: "0.75rem", fontWeight: 700, color: "#aaa", textTransform: "uppercase", letterSpacing: "0.05em" }}>Interactions ({contact.interactions?.length || 0})</div>
            <button onClick={onAddInteraction} style={{ ...btnPrimary, fontSize: "0.78rem", padding: "0.25rem 0.65rem" }}>+ Log Interaction</button>
          </div>
          {sortedInteractions.length === 0 ? (
            <p style={{ color: "#ccc", fontSize: "0.83rem", textAlign: "center", padding: "0.75rem 0" }}>No interactions yet.</p>
          ) : (
            <div style={{ display: "flex", flexDirection: "column", gap: "0.4rem" }}>
              {sortedInteractions.map(interaction => {
                const itype = INTERACTION_TYPES.find(i => i.value === interaction.type);
                return (
                  <div key={interaction.id} style={{ display: "flex", gap: "0.55rem", alignItems: "flex-start", padding: "0.5rem 0.65rem", background: "#f9f9f9", borderRadius: 8, border: "1px solid #f0f0f0" }}>
                    <span style={{ fontSize: "0.9rem", flexShrink: 0 }}>{itype ? itype.label.split(" ")[0] : "📋"}</span>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: "0.83rem", color: "#333", lineHeight: 1.35 }}>{interaction.summary}</div>
                      <div style={{ fontSize: "0.72rem", color: "#aaa", marginTop: "0.15rem" }}>{new Date(interaction.date).toLocaleDateString([], { weekday: "short", month: "short", day: "numeric" })}</div>
                    </div>
                    <button onClick={() => onDeleteInteraction(interaction.id)} style={{ background: "none", border: "none", color: "#ccc", cursor: "pointer", fontSize: "0.85rem", padding: "0.1rem", flexShrink: 0 }}>✕</button>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Client Portal */}
        <div>
          <div style={{ fontSize: "0.75rem", fontWeight: 700, color: "#aaa", textTransform: "uppercase", letterSpacing: "0.05em", marginBottom: "0.5rem" }}>Client Portal</div>
          {portalLoading ? (
            <p style={{ color: "#ccc", fontSize: "0.8rem" }}>Loading…</p>
          ) : portal ? (
            <div>
              <div style={{ display: "flex", alignItems: "center", gap: "0.5rem", marginBottom: "0.6rem" }}>
                <span style={{ fontSize: "0.72rem", fontWeight: 700, padding: "0.2rem 0.55rem", borderRadius: 12, background: portal.isActive ? "#dcfce7" : "#f3f4f6", color: portal.isActive ? "#16a34a" : "#9ca3af" }}>{portal.isActive ? "Active" : "Inactive"}</span>
                <span style={{ fontSize: "0.75rem", color: "#888" }}>{Object.values(portal.enabledSections || {}).filter(Boolean).length} sections enabled</span>
              </div>
              <div style={{ display: "flex", gap: "0.4rem", flexWrap: "wrap" }}>
                <button onClick={sendPortalLink} disabled={sendingLink || !contact.email} style={{ ...btnPrimary, fontSize: "0.77rem", padding: "0.3rem 0.7rem" }}>{sendingLink ? "Sending…" : "📧 Send link"}</button>
                <a href={`/portal/${portal.token}`} target="_blank" rel="noopener noreferrer" style={{ ...btnGhost, fontSize: "0.77rem", padding: "0.3rem 0.7rem", textDecoration: "none", display: "inline-flex", alignItems: "center" }}>🌐 Preview</a>
              </div>
              {portalActivity.length > 0 && (
                <div style={{ marginTop: "0.75rem" }}>
                  <div style={{ fontSize: "0.7rem", fontWeight: 700, color: "#bbb", textTransform: "uppercase", letterSpacing: "0.05em", marginBottom: "0.3rem" }}>Recent activity</div>
                  <div style={{ display: "flex", flexDirection: "column", gap: "0.25rem" }}>
                    {portalActivity.map(a => (
                      <div key={a.id} style={{ fontSize: "0.78rem", color: "#666", display: "flex", justifyContent: "space-between", gap: "0.5rem" }}>
                        <span>{activityLabels[a.action] || a.action}</span>
                        <span style={{ color: "#bbb", flexShrink: 0 }}>{new Date(a.createdAt).toLocaleDateString("en-GB", { day: "numeric", month: "short" })}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          ) : (
            <div>
              <p style={{ margin: "0 0 0.5rem", fontSize: "0.83rem", color: "#888", lineHeight: 1.5 }}>Give this contact their own branded, secure dashboard.</p>
              <button onClick={enablePortal} disabled={enablingPortal} style={{ ...btnPrimary, fontSize: "0.8rem", padding: "0.4rem 0.9rem" }}>{enablingPortal ? "Enabling…" : "🌐 Enable Portal"}</button>
            </div>
          )}
        </div>

        <div style={{ fontSize: "0.72rem", color: "#ccc", borderTop: "1px solid #f0f0f0", paddingTop: "0.65rem" }}>
          Added {new Date(contact.createdAt).toLocaleDateString([], { month: "long", day: "numeric", year: "numeric" })}
        </div>
      </div>
    </div>
  );
}

// ─── ContactCard ──────────────────────────────────────────────────────────────
function ContactCard({ contact, stage, allStages, boardColumns, isSelected, onSelect, nudge, onDragStart, onTouchDragStart, onTouchDrop, onMoveToStage, onRemoveFromBoard, isTourAnchor }) {
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef(null);

  useEffect(() => {
    if (!menuOpen) return;
    function outside(e) {
      if (menuRef.current && !menuRef.current.contains(e.target)) setMenuOpen(false);
    }
    document.addEventListener("mousedown", outside);
    return () => document.removeEventListener("mousedown", outside);
  }, [menuOpen]);

  const customPills = useMemo(() => {
    const cf = contact.boardEntry?.customFields || {};
    return (boardColumns || [])
      .filter(col => cf[col.fieldKey] != null && cf[col.fieldKey] !== "" && cf[col.fieldKey] !== false)
      .slice(0, 3)
      .map(col => ({ label: col.name, value: String(cf[col.fieldKey]) }));
  }, [contact.boardEntry, boardColumns]);

  return (
    <div
      draggable
      onDragStart={e => { onDragStart(e, contact.id); }}
      onTouchStart={e => {
        if (e.target.closest('button')) return;
        let isDragging = false;
        let overStageId = null;
        const startX = e.touches[0].clientX;
        const startY = e.touches[0].clientY;
        const cardEl = e.currentTarget;

        const onMove = (ev) => {
          const touch = ev.touches[0];
          if (!isDragging && Math.hypot(touch.clientX - startX, touch.clientY - startY) < 8) return;
          if (!isDragging) {
            isDragging = true;
            onTouchDragStart(contact.id);
          }
          ev.preventDefault();
          cardEl.style.opacity = '0.55';
          cardEl.style.transform = 'scale(1.03)';
          cardEl.style.pointerEvents = 'none';
          const below = document.elementFromPoint(touch.clientX, touch.clientY);
          cardEl.style.pointerEvents = '';
          let node = below;
          while (node && !node.dataset?.stageId) node = node.parentElement;
          overStageId = node?.dataset?.stageId ?? null;
        };

        const onEnd = () => {
          document.removeEventListener('touchmove', onMove);
          document.removeEventListener('touchend', onEnd);
          cardEl.style.opacity = '';
          cardEl.style.transform = '';
          if (isDragging) onTouchDrop(overStageId);
        };

        document.addEventListener('touchmove', onMove, { passive: false });
        document.addEventListener('touchend', onEnd);
      }}
      onClick={() => { if (!menuOpen) onSelect(contact.id); }}
      data-tour-id={isTourAnchor ? "crm-contact-card" : undefined}
      style={{ position: "relative", padding: "0.6rem 0.75rem", minHeight: 44, background: isSelected ? "#f0f5f9" : "#fff", border: isSelected ? `1.5px solid ${NAVY}` : "1px solid #e0e0e0", borderRadius: 8, cursor: "pointer", boxShadow: "0 1px 3px rgba(0,0,0,0.04)", transition: "border-color 0.12s" }}
    >
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: "0.3rem" }}>
        <span style={{ display: "flex", alignItems: "center", gap: "0.3rem", fontWeight: 700, fontSize: "0.85rem", color: "#1a1a1a", lineHeight: 1.3, minWidth: 0, flex: 1 }}>
          {nudge && <NudgeIcon urgency={nudge.urgency} reason={nudge.reason} />}
          <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{contact.name}</span>
        </span>
        <div style={{ display: "flex", alignItems: "center", gap: 4, flexShrink: 0 }}>
          {(contact.tags || []).includes("Client") && <HealthScoreBadge score={contact.healthScore} scoredAt={contact.healthScoreAt} data-tour-id={isTourAnchor ? "crm-health-badge" : undefined} />}
          <div ref={menuRef} style={{ position: "relative" }}>
            <button
              onClick={e => { e.stopPropagation(); setMenuOpen(v => !v); }}
              style={{ background: "none", border: "none", cursor: "pointer", color: "#bbb", fontSize: "0.9rem", padding: "0 2px", lineHeight: 1 }}
            >•••</button>
            {menuOpen && (
              <div style={{ position: "absolute", right: 0, top: "100%", zIndex: 200, background: "#fff", border: "1px solid #e0e0e0", borderRadius: 8, boxShadow: "0 4px 16px rgba(0,0,0,0.12)", minWidth: 160, padding: "4px 0" }}>
                {allStages.filter(s => s.id !== stage?.id).map(s => (
                  <button key={s.id} onClick={e => { e.stopPropagation(); onMoveToStage(contact.id, s.id); setMenuOpen(false); }} style={{ display: "block", width: "100%", padding: "7px 12px", border: "none", background: "transparent", textAlign: "left", fontSize: 12, cursor: "pointer", color: "#333", fontFamily: FONT }}>
                    → {s.name}
                  </button>
                ))}
                <div style={{ height: 1, background: "#f0f0f0", margin: "4px 0" }} />
                <button onClick={e => { e.stopPropagation(); onRemoveFromBoard(contact.id); setMenuOpen(false); }} style={{ display: "block", width: "100%", padding: "7px 12px", border: "none", background: "transparent", textAlign: "left", fontSize: 12, cursor: "pointer", color: "#ef4444", fontFamily: FONT }}>
                  Remove from board
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
      {contact.company && <div style={{ fontSize: "0.72rem", color: "#888", marginTop: "0.1rem", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{contact.company}</div>}
      {contact.email && <div style={{ fontSize: "0.7rem", color: "#bbb", marginTop: "0.05rem", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{contact.email}</div>}
      {contact.isNew && <span style={{ display: "inline-block", marginTop: "0.25rem", background: "#fde8e8", color: "#A12710", border: "1px solid #A12710", borderRadius: 6, padding: "0 0.35rem", fontSize: "0.62rem", fontWeight: 700 }}>NEW</span>}
      {customPills.length > 0 && (
        <div style={{ display: "flex", flexWrap: "wrap", gap: "0.25rem", marginTop: "0.3rem" }}>
          {customPills.map((p, i) => (
            <span key={i} style={{ fontSize: "0.65rem", background: "#f0f4ff", color: "#4b63cc", border: "1px solid #c7d2fe", borderRadius: 8, padding: "0.05rem 0.4rem", fontWeight: 600 }}>
              {p.label}: {p.value.length > 14 ? p.value.slice(0, 14) + "…" : p.value}
            </span>
          ))}
        </div>
      )}
    </div>
  );
}

// ─── KanbanColumn ─────────────────────────────────────────────────────────────
function KanbanColumn({ stage, boardColumns, allStages, selectedId, onSelect, nudges, isMobile, draggingId, onDragStart, onDrop, onMoveToStage, onRemoveFromBoard, onTouchDragStart, onTouchDrop, isFirstColumn }) {
  const [over, setOver] = useState(false);
  const contacts = stage.contacts || [];

  return (
    <div
      data-stage-id={stage.id}
      onDragOver={e => { e.preventDefault(); setOver(true); }}
      onDragLeave={() => setOver(false)}
      onDrop={e => { e.preventDefault(); setOver(false); onDrop(e, stage.id); }}
      style={{ minWidth: isMobile ? 260 : 200, maxWidth: isMobile ? 260 : 220, display: "flex", flexDirection: "column", gap: "0.5rem", flexShrink: 0, transition: "background 0.1s", background: over ? "#f0f5ff" : "transparent", borderRadius: 10, padding: over ? "6px" : 0 }}
    >
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", paddingBottom: "0.5rem", borderBottom: `2px solid ${stage.colour || "#888"}` }}>
        <span style={{ fontSize: "0.8rem", fontWeight: 700, color: stage.colour || "#888", textTransform: "uppercase", letterSpacing: "0.04em" }}>{stage.name}</span>
        <span style={{ fontSize: "0.72rem", fontWeight: 700, color: "#fff", background: stage.colour || "#888", borderRadius: 10, padding: "0.05rem 0.5rem", minWidth: 18, textAlign: "center" }}>{contacts.length}</span>
      </div>
      <div style={{ display: "flex", flexDirection: "column", gap: "0.4rem", minHeight: 60 }}>
        {contacts.length === 0 && (
          <div style={{ textAlign: "center", color: "#ddd", fontSize: "0.78rem", padding: "0.75rem 0", fontStyle: "italic" }}>
            {over ? "Drop here" : "Empty"}
          </div>
        )}
        {contacts.map((contact, i) => (
          <ContactCard
            key={contact.id}
            contact={contact}
            stage={stage}
            allStages={allStages}
            boardColumns={boardColumns}
            isSelected={contact.id === selectedId}
            onSelect={onSelect}
            nudge={nudges?.[contact.id]}
            onDragStart={onDragStart}
            onTouchDragStart={onTouchDragStart}
            onTouchDrop={onTouchDrop}
            onMoveToStage={onMoveToStage}
            onRemoveFromBoard={onRemoveFromBoard}
            isTourAnchor={isFirstColumn && i === 0}
          />
        ))}
      </div>
    </div>
  );
}

// ─── Main component ───────────────────────────────────────────────────────────
export default function CRMDashboard({ currentUser, onNavigateUpgrade }) {
  useAutoTour('crm');
  const [isMobile, setIsMobile] = useState(typeof window !== "undefined" && window.innerWidth < 768);
  useEffect(() => {
    function check() { setIsMobile(window.innerWidth < 768); }
    window.addEventListener("resize", check);
    return () => window.removeEventListener("resize", check);
  }, []);

  const { contacts, setContacts } = useContacts();
  const { invoices } = useInvoices();
  const [tasks, setTasks]         = useState([]);

  // Board state
  const [boards, setBoards]             = useState([]);
  const [activeBoardId, setActiveBoardId] = useState(null);
  const [boardStages, setBoardStages]   = useState([]);   // [{id, name, colour, contacts:[]}]
  const [boardColumns, setBoardColumns] = useState([]);   // [{id, name, fieldKey, fieldType, options}]
  const [boardLoading, setBoardLoading] = useState(true);

  // UI state
  const [selectedId, setSelectedId]             = useState(null);
  const [search, setSearch]                     = useState("");
  const [draggingId, setDraggingId]             = useState(null);
  const touchDragContactId                       = useRef(null);
  const [showCreateBoard, setShowCreateBoard]   = useState(false);
  const [showBoardSettings, setShowBoardSettings] = useState(false);
  const [showInteractionModal, setShowInteractionModal] = useState(false);
  const [showAddContact, setShowAddContact]     = useState(false);
  const [migrationToast, setMigrationToast]     = useState(null);
  const [nudgesByContactId, setNudgesByContactId] = useState({});

  const { scores, recalculate } = useClientHealth({ contacts, tasks, invoices });

  // ── Load tasks + nudges ────────────────────────────────────────────────────
  useEffect(() => {
    api("/api/tasks").then(r => r.json()).then(d => setTasks(d.tasks || [])).catch(() => {});
    api("/api/follow-up/nudges").then(r => r.json()).then(d => {
      const map = {};
      for (const n of (d.nudges || [])) { if (!map[n.contactId]) map[n.contactId] = n; }
      setNudgesByContactId(map);
    }).catch(() => {});
  }, []);

  // ── Fetch board data ───────────────────────────────────────────────────────
  const fetchBoardData = useCallback(async (boardId) => {
    if (!boardId) return;
    setBoardLoading(true);
    try {
      const [contactsRes, detailRes] = await Promise.all([
        api(`/api/crm-boards/${boardId}/contacts`),
        api(`/api/crm-boards/${boardId}`),
      ]);
      if (contactsRes.ok) {
        const d = await contactsRes.json();
        // Merge unassigned into first stage
        const stages = d.stages || [];
        if (d.unassigned?.length && stages.length) {
          stages[0] = { ...stages[0], contacts: [...(stages[0].contacts || []), ...d.unassigned] };
        }
        setBoardStages(stages);
      }
      if (detailRes.ok) {
        const d = await detailRes.json();
        setBoardColumns(d.columns || []);
      }
    } catch (err) {
      console.error("[CRMDashboard] fetchBoardData failed:", err);
    } finally {
      setBoardLoading(false);
    }
  }, []);

  // ── Load boards on mount, migrate if empty ────────────────────────────────
  useEffect(() => {
    async function init() {
      try {
        const r = await api("/api/crm-boards");
        if (!r.ok) return;
        const d = await r.json();
        let bList = d.boards || [];

        if (bList.length === 0) {
          // Trigger legacy migration
          const migRes = await api("/api/crm-boards/migrate-legacy", { method: "POST" });
          if (migRes.ok) {
            const migData = await migRes.json();
            if (!migData.skipped) {
              setMigrationToast(`Migrated ${migData.contactsMigrated} contact${migData.contactsMigrated !== 1 ? "s" : ""} to your new CRM board`);
              setTimeout(() => setMigrationToast(null), 5000);
            }
            // Refetch boards
            const r2 = await api("/api/crm-boards");
            if (r2.ok) { const d2 = await r2.json(); bList = d2.boards || []; }
          }
        }

        setBoards(bList);
        const defaultBoard = bList.find(b => b.isDefault) || bList[0];
        if (defaultBoard) {
          setActiveBoardId(defaultBoard.id);
        }
      } catch (err) {
        console.error("[CRMDashboard] init failed:", err);
      }
    }
    init();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Fetch board data when activeBoardId changes ───────────────────────────
  useEffect(() => {
    if (activeBoardId) fetchBoardData(activeBoardId);
  }, [activeBoardId, fetchBoardData]);

  // ── Filtered stages (search + 60-day signed-client expiry) ──────────────
  const SIXTY_DAYS_MS = 60 * 24 * 60 * 60 * 1000;
  const filteredBoardStages = useMemo(() => {
    const q = search.trim().toLowerCase();
    return boardStages.map(stage => ({
      ...stage,
      contacts: (stage.contacts || []).filter(c => {
        if (c.contractSignedAt && (Date.now() - new Date(c.contractSignedAt).getTime()) > SIXTY_DAYS_MS) return false;
        if (!q) return true;
        return c.name?.toLowerCase().includes(q) || c.email?.toLowerCase().includes(q) || c.company?.toLowerCase().includes(q);
      }),
    }));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [boardStages, search]);

  // ── Selected contact (from board stages) ──────────────────────────────────
  const selectedContact = useMemo(() => {
    for (const stage of boardStages) {
      const c = (stage.contacts || []).find(c => c.id === selectedId);
      if (c) return c;
    }
    return null;
  }, [boardStages, selectedId]);

  const selectedContactStage = useMemo(() => {
    if (!selectedContact) return null;
    return boardStages.find(s => (s.contacts || []).some(c => c.id === selectedId)) || null;
  }, [boardStages, selectedId, selectedContact]);

  // ── Drag and drop ─────────────────────────────────────────────────────────
  function handleDragStart(e, contactId) {
    touchDragContactId.current = contactId;
    setDraggingId(contactId);
    e.dataTransfer.effectAllowed = "move";
  }

  function handleTouchDragStart(contactId) {
    touchDragContactId.current = contactId;
    setDraggingId(contactId);
  }

  async function handleTouchDrop(targetStageId) {
    const contactId = touchDragContactId.current;
    touchDragContactId.current = null;
    setDraggingId(null);
    if (contactId && targetStageId) await handleMoveToStage(contactId, targetStageId);
  }

  async function handleDrop(e, targetStageId) {
    if (!draggingId || !activeBoardId) { setDraggingId(null); return; }
    const fromContactId = draggingId;
    setDraggingId(null);

    // Optimistic update
    setBoardStages(prev => {
      const contactObj = prev.flatMap(s => s.contacts || []).find(c => c.id === fromContactId);
      if (!contactObj) return prev;
      return prev.map(stage => ({
        ...stage,
        contacts: stage.id === targetStageId
          ? [...(stage.contacts || []).filter(c => c.id !== fromContactId), { ...contactObj, boardEntry: { ...(contactObj.boardEntry || {}), stageId: targetStageId } }]
          : (stage.contacts || []).filter(c => c.id !== fromContactId),
      }));
    });

    try {
      await api(`/api/crm-boards/${activeBoardId}/contacts/${fromContactId}/stage`, {
        method: "PATCH",
        body: JSON.stringify({ stageId: targetStageId }),
      });
    } catch (err) {
      console.error("[CRMDashboard] drop stage update failed:", err);
      fetchBoardData(activeBoardId); // revert on failure
    }
  }

  async function handleMoveToStage(contactId, stageId) {
    if (!activeBoardId) return;
    setBoardStages(prev => {
      const contactObj = prev.flatMap(s => s.contacts || []).find(c => c.id === contactId);
      if (!contactObj) return prev;
      return prev.map(stage => ({
        ...stage,
        contacts: stage.id === stageId
          ? [...(stage.contacts || []).filter(c => c.id !== contactId), { ...contactObj, boardEntry: { ...(contactObj.boardEntry || {}), stageId } }]
          : (stage.contacts || []).filter(c => c.id !== contactId),
      }));
    });
    try {
      await api(`/api/crm-boards/${activeBoardId}/contacts/${contactId}/stage`, { method: "PATCH", body: JSON.stringify({ stageId }) });
    } catch (err) {
      console.error("[CRMDashboard] move stage failed:", err);
      fetchBoardData(activeBoardId);
    }
  }

  async function handleBoardStageChange(contactId, stageId) {
    await handleMoveToStage(contactId, stageId);
  }

  async function handleRemoveFromBoard(contactId) {
    if (!activeBoardId) return;
    setBoardStages(prev => prev.map(s => ({ ...s, contacts: (s.contacts || []).filter(c => c.id !== contactId) })));
    if (selectedId === contactId) setSelectedId(null);
    try {
      await api(`/api/crm-boards/${activeBoardId}/contacts/${contactId}`, { method: "DELETE" });
    } catch (err) {
      console.error("[CRMDashboard] remove from board failed:", err);
      fetchBoardData(activeBoardId);
    }
  }

  // ── Field change ──────────────────────────────────────────────────────────
  async function handleFieldChange(contactId, fieldKey, value) {
    if (!activeBoardId) return;
    setBoardStages(prev => prev.map(stage => ({
      ...stage,
      contacts: (stage.contacts || []).map(c => c.id !== contactId ? c : {
        ...c,
        boardEntry: { ...(c.boardEntry || {}), customFields: { ...(c.boardEntry?.customFields || {}), [fieldKey]: value } },
      }),
    })));
    try {
      await api(`/api/crm-boards/${activeBoardId}/contacts/${contactId}/fields`, {
        method: "PATCH",
        body: JSON.stringify({ customFields: { [fieldKey]: value } }),
      });
    } catch (err) {
      console.error("[CRMDashboard] field change failed:", err);
    }
  }

  // ── Add contact ───────────────────────────────────────────────────────────
  async function saveContact(contactData) {
    const payload = { ...contactData, crmStage: "Lead", interactions: [], source: contactData.source || "manual" };
    const res = await api("/api/contacts", { method: "POST", body: JSON.stringify(payload) });
    if (!res.ok) { const err = await res.json().catch(() => ({})); throw new Error(err.error || "Failed to create contact"); }
    const data = await res.json();

    // Resolve the board to add the contact to — fetch if not yet loaded
    let boardId = activeBoardId;
    if (!boardId) {
      const br = await api("/api/crm-boards");
      if (br.ok) {
        const bd = await br.json();
        const first = (bd.boards || []).find(b => b.isDefault) || (bd.boards || [])[0];
        if (first) {
          boardId = first.id;
          setBoards(bd.boards || []);
          setActiveBoardId(first.id);
        }
      }
    }

    if (boardId) {
      await api(`/api/crm-boards/${boardId}/contacts`, {
        method: "POST",
        body: JSON.stringify({ contactId: data.contact.id }),
      });
      await fetchBoardData(boardId);
      setSelectedId(data.contact.id);
    }

    setContacts(prev => [data.contact, ...prev]);
    setShowAddContact(false);
  }

  // ── Interaction management ────────────────────────────────────────────────
  function addInteraction(interaction) {
    if (!selectedId) return;
    const updatedContacts = contacts.map(c => {
      if (c.id !== selectedId) return c;
      const updated = { ...c, interactions: [interaction, ...(c.interactions || [])] };
      api(`/api/contacts/${selectedId}`, { method: "PUT", body: JSON.stringify({ interactions: updated.interactions }) }).catch(() => {});
      return updated;
    });
    setContacts(updatedContacts);
    // Mirror into boardStages
    setBoardStages(prev => prev.map(stage => ({
      ...stage,
      contacts: (stage.contacts || []).map(c => {
        if (c.id !== selectedId) return c;
        return { ...c, interactions: [interaction, ...(c.interactions || [])] };
      }),
    })));
    setShowInteractionModal(false);
    setTimeout(recalculate, 50);
  }

  function deleteInteraction(interactionId) {
    if (!selectedId) return;
    setContacts(prev => prev.map(c => {
      if (c.id !== selectedId) return c;
      const updated = { ...c, interactions: (c.interactions || []).filter(i => i.id !== interactionId) };
      api(`/api/contacts/${selectedId}`, { method: "PUT", body: JSON.stringify({ interactions: updated.interactions }) }).catch(() => {});
      return updated;
    }));
    setBoardStages(prev => prev.map(stage => ({
      ...stage,
      contacts: (stage.contacts || []).map(c => c.id !== selectedId ? c : { ...c, interactions: (c.interactions || []).filter(i => i.id !== interactionId) }),
    })));
  }

  // ── Create board ──────────────────────────────────────────────────────────
  async function handleCreateBoard({ name, description, boardType, workflowDraft }) {
    const res = await api("/api/crm-boards", { method: "POST", body: JSON.stringify({ name, description, boardType }) });
    if (!res.ok) throw new Error("Failed to create board");
    const data = await res.json();
    const newBoardId = data.board.id;

    // If custom workflow, post stages + columns
    if (workflowDraft?.stages?.length) {
      for (const stage of workflowDraft.stages) {
        await api(`/api/crm-boards/${newBoardId}/stages`, { method: "POST", body: JSON.stringify({ name: stage.name, colour: stage.colour || "#94a3b8", position: stage.position ?? 0, isTerminal: stage.isTerminal ?? false, isStart: stage.isStart ?? false }) });
      }
    }
    if (workflowDraft?.columns?.length) {
      for (const col of workflowDraft.columns) {
        await api(`/api/crm-boards/${newBoardId}/columns`, { method: "POST", body: JSON.stringify({ name: col.name, fieldKey: col.fieldKey, fieldType: col.fieldType || "TEXT", options: col.options ?? null, position: col.position ?? 0, isRequired: col.isRequired ?? false }) });
      }
    }

    // Refetch boards and switch
    const r2 = await api("/api/crm-boards");
    if (r2.ok) { const d2 = await r2.json(); setBoards(d2.boards || []); }
    setActiveBoardId(newBoardId);
    setShowCreateBoard(false);
  }

  // ── Delete board (from settings) ──────────────────────────────────────────
  async function handleBoardDeleted() {
    const r = await api("/api/crm-boards");
    if (r.ok) {
      const d = await r.json();
      const bList = d.boards || [];
      setBoards(bList);
      const next = bList.find(b => b.isDefault) || bList[0];
      setActiveBoardId(next?.id ?? null);
    }
    setShowBoardSettings(false);
  }

  // ── Pipeline summary ───────────────────────────────────────────────────────
  const totalContacts = boardStages.reduce((s, st) => s + (st.contacts?.length || 0), 0);
  const activeBoard   = boards.find(b => b.id === activeBoardId);

  const pipelineStats = useMemo(() => {
    const allContacts = boardStages.flatMap(s => s.contacts || []);
    const now = new Date();
    const thisMonthStart = new Date(now.getFullYear(), now.getMonth(), 1);
    const lastMonthStart = new Date(now.getFullYear(), now.getMonth() - 1, 1);
    let thisMonth = 0, lastMonth = 0, activeCount = 0;
    for (const c of allContacts) {
      if (!c.contractSignedAt) { activeCount++; continue; }
      const d = new Date(c.contractSignedAt);
      if (d >= thisMonthStart) thisMonth++;
      else if (d >= lastMonthStart) lastMonth++;
    }
    return { thisMonth, lastMonth, activeCount };
  }, [boardStages]);

  return (
    <div style={{ padding: isMobile ? "0.75rem" : "1rem", fontFamily: FONT, display: "flex", flexDirection: "column", minHeight: isMobile ? "auto" : "calc(100vh - 4rem)", overflowX: "hidden" }}>

      {/* Migration toast */}
      {migrationToast && (
        <div style={{ position: "fixed", bottom: 24, left: "50%", transform: "translateX(-50%)", zIndex: 3000, background: NAVY, color: "#fff", padding: "10px 20px", borderRadius: 10, fontSize: 13, fontWeight: 600, boxShadow: "0 4px 16px rgba(0,0,0,0.2)", fontFamily: FONT }}>
          ✓ {migrationToast}
        </div>
      )}

      {/* Header */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "0.75rem", flexWrap: "wrap", gap: "0.75rem", flexShrink: 0 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <h2 style={{ margin: 0, fontSize: "1.4rem" }}>CRM Pipeline</h2>
          <HelpButton tourId="crm" />
        </div>
        <div style={{ display: "flex", gap: "0.75rem", alignItems: "center", flexWrap: "wrap", width: isMobile ? "100%" : "auto" }}>
          <input data-tour-id="crm-search" type="text" placeholder="Search contacts..." value={search} onChange={e => setSearch(e.target.value)} style={{ padding: "0.5rem 0.85rem", borderRadius: 8, border: "1px solid #ddd", fontSize: "0.9rem", width: isMobile ? "100%" : 220, boxSizing: "border-box", minHeight: 44 }} />
          <button data-tour-id="crm-add-contact" onClick={() => setShowAddContact(true)} style={{ padding: "0.5rem 1.1rem", background: NAVY, color: "#fff", border: "none", borderRadius: 8, fontWeight: 700, fontSize: "0.88rem", cursor: "pointer", fontFamily: FONT, whiteSpace: "nowrap", width: isMobile ? "100%" : "auto", minHeight: 44 }}>
            + Add Contact
          </button>
        </div>
      </div>

      {/* Board switcher */}
      <div data-tour-id="crm-board-selector" style={{ display: "flex", alignItems: "center", borderBottom: `1px solid ${BORDER}`, marginBottom: "0.75rem", flexShrink: 0, background: "#fff", borderRadius: "10px 10px 0 0", padding: "0 4px" }}>
        <div style={{ flex: 1, display: "flex", overflowX: "auto", scrollbarWidth: "none" }}>
          {boards.map(board => (
            <button
              key={board.id}
              onClick={() => { setActiveBoardId(board.id); setSelectedId(null); }}
              style={{ padding: "10px 16px", background: "none", border: "none", borderBottom: activeBoardId === board.id ? `2px solid ${NAVY}` : "2px solid transparent", fontFamily: FONT, fontSize: 13, fontWeight: activeBoardId === board.id ? 700 : 500, color: activeBoardId === board.id ? NAVY : "#888", cursor: "pointer", whiteSpace: "nowrap", flexShrink: 0, marginBottom: -1 }}
            >
              {board.name.length > 20 ? board.name.slice(0, 20) + "…" : board.name}
            </button>
          ))}
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 6, padding: "0 8px", flexShrink: 0 }}>
          {activeBoardId && (
            <button onClick={() => setShowBoardSettings(true)} title="Board settings" style={{ background: "none", border: `1px solid ${BORDER}`, borderRadius: 6, padding: "5px 8px", cursor: "pointer", fontSize: 14, color: "#888" }}>⚙</button>
          )}
          <button onClick={() => setShowCreateBoard(true)} style={{ padding: "7px 12px", borderRadius: 7, border: "none", background: NAVY, color: "#fff", fontSize: 12, fontWeight: 700, cursor: "pointer", fontFamily: FONT, whiteSpace: "nowrap" }}>
            + New board
          </button>
        </div>
      </div>

      {/* Board summary */}
      {activeBoard && (
        <div data-tour-id="crm-pipeline-stats" style={{ fontSize: "0.82rem", color: "#888", marginBottom: "0.75rem", flexShrink: 0, display: "flex", flexWrap: "wrap", gap: "0 1rem", alignItems: "center" }}>
          <span>{totalContacts} contact{totalContacts !== 1 ? "s" : ""} · {boardStages.length} stage{boardStages.length !== 1 ? "s" : ""}</span>
          {pipelineStats.activeCount > 0 && <span style={{ color: "#6366f1", fontWeight: 600 }}>{pipelineStats.activeCount} active</span>}
          {pipelineStats.thisMonth > 0 && <span style={{ color: "#16a34a", fontWeight: 600 }}>{pipelineStats.thisMonth} signed this month</span>}
          {pipelineStats.lastMonth > 0 && <span style={{ color: "#888" }}>{pipelineStats.lastMonth} signed last month</span>}
          {activeBoard.description && <span style={{ color: "#bbb" }}>— {activeBoard.description}</span>}
        </div>
      )}

      {/* Board body */}
      <div data-tour-id="crm-kanban-board" style={{ flex: 1, display: "flex", overflow: "hidden", gap: 0, border: `1px solid ${BORDER}`, borderRadius: 12, background: "#fafafa", minHeight: isMobile ? "auto" : 0 }}>
        {boardLoading ? (
          <div style={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center", color: "#bbb", fontSize: "0.9rem" }}>Loading…</div>
        ) : totalContacts === 0 && !search ? (
          <div style={{ flex: 1, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: "0.75rem", color: "#bbb", padding: "3rem" }}>
            <div style={{ fontSize: "3.5rem" }}>👥</div>
            <div style={{ fontSize: "1.1rem", fontWeight: 700, color: "#999" }}>No contacts on this board</div>
            <div style={{ fontSize: "0.9rem", color: "#bbb", textAlign: "center", maxWidth: 300 }}>Add your first contact to get started with your CRM pipeline</div>
            <button onClick={() => setShowAddContact(true)} style={{ marginTop: "0.5rem", padding: "0.65rem 1.5rem", background: NAVY, color: "#fff", border: "none", borderRadius: 8, fontWeight: 700, fontSize: "0.9rem", cursor: "pointer", fontFamily: FONT }}>+ Add Contact</button>
            <button
              onClick={() => setShowBoardSettings(true)}
              style={{ background: "none", border: "none", cursor: "pointer", color: NAVY, fontSize: "0.83rem", fontFamily: FONT, textDecoration: "underline", padding: 0 }}
            >
              Edit stages &amp; columns →
            </button>
          </div>
        ) : (
          <>
            {/* Kanban board */}
            <div style={{ flex: 1, overflowX: "auto", overflowY: "auto", padding: isMobile ? "0.75rem" : "1rem" }}>
              <div style={{ display: "flex", gap: "1rem", minWidth: "max-content", alignItems: "flex-start" }}>
                {filteredBoardStages.map((stage, stageIdx) => (
                  <KanbanColumn
                    key={stage.id}
                    stage={stage}
                    boardColumns={boardColumns}
                    allStages={boardStages}
                    selectedId={selectedId}
                    onSelect={id => setSelectedId(prev => prev === id ? null : id)}
                    nudges={nudgesByContactId}
                    isMobile={isMobile}
                    draggingId={draggingId}
                    onDragStart={handleDragStart}
                    onDrop={handleDrop}
                    onTouchDragStart={handleTouchDragStart}
                    onTouchDrop={handleTouchDrop}
                    onMoveToStage={handleMoveToStage}
                    onRemoveFromBoard={handleRemoveFromBoard}
                    isFirstColumn={stageIdx === 0}
                  />
                ))}
              </div>
            </div>

            {/* Detail panel */}
            {selectedContact && (
              <ContactDetailPanel
                contact={selectedContact}
                health={scores[selectedContact.id] ?? null}
                boardStages={boardStages}
                boardColumns={boardColumns}
                boardEntry={selectedContact.boardEntry}
                boardId={activeBoardId}
                onClose={() => setSelectedId(null)}
                onAddInteraction={() => setShowInteractionModal(true)}
                onDeleteInteraction={deleteInteraction}
                onLogContact={addInteraction}
                onRefreshHealth={recalculate}
                onBoardStageChange={handleBoardStageChange}
                onFieldChange={handleFieldChange}
                isMobile={isMobile}
              />
            )}
          </>
        )}
      </div>

      {/* Modals */}
      {showInteractionModal && <AddInteractionModal onSave={addInteraction} onClose={() => setShowInteractionModal(false)} />}
      {showAddContact && <ContactFormModal onSave={saveContact} onClose={() => setShowAddContact(false)} />}
      {showCreateBoard && <CreateCrmBoardModal onSave={handleCreateBoard} onClose={() => setShowCreateBoard(false)} />}
      {showBoardSettings && activeBoardId && (
        <CrmBoardSettings
          board={activeBoard}
          activeBoardId={activeBoardId}
          canDelete={boards.length > 1}
          onClose={() => setShowBoardSettings(false)}
          onSaved={async () => {
            const r = await api("/api/crm-boards");
            if (r.ok) { const d = await r.json(); setBoards(d.boards || []); }
            await fetchBoardData(activeBoardId);
            setShowBoardSettings(false);
          }}
          onDeleted={handleBoardDeleted}
        />
      )}
    </div>
  );
}

// ─── Shared styles ────────────────────────────────────────────────────────────
const inputStyle = {
  display: "block", width: "100%", marginTop: "0.3rem",
  padding: "0.55rem 0.75rem", borderRadius: 6, border: "1px solid #ddd",
  fontSize: "0.95rem", boxSizing: "border-box", fontFamily: FONT,
};

const labelStyle = {
  display: "block", fontSize: "0.82rem", fontWeight: 600, color: "#555",
};

const formLabel = {
  display: "block", fontSize: 11, fontWeight: 700, color: "#6b7280",
  marginBottom: 5, textTransform: "uppercase", letterSpacing: "0.04em", fontFamily: FONT,
};

const formInput = {
  width: "100%", padding: "9px 12px", border: `1px solid ${BORDER}`, borderRadius: 8,
  fontSize: 14, boxSizing: "border-box", outline: "none", background: "#fff",
  fontFamily: FONT, color: "#111",
};

const btnPrimary = {
  padding: "0.5rem 1.1rem", background: "#E4E69B", border: "none",
  borderRadius: 7, fontWeight: 700, fontSize: "0.88rem", cursor: "pointer", fontFamily: FONT,
};

const btnGhost = {
  padding: "0.5rem 1.1rem", background: "transparent", border: "1px solid #ddd",
  borderRadius: 7, fontWeight: 500, fontSize: "0.88rem", cursor: "pointer", fontFamily: FONT, color: "#888",
};

const btnSecondary = {
  padding: "0.35rem 0.75rem", background: "#f5f5f5", border: "1px solid #ddd",
  borderRadius: 7, fontWeight: 600, fontSize: "0.85rem", cursor: "pointer", fontFamily: FONT, color: "#555",
};
